// "Adequar ao lance final": leva os unitários calculados pelo backend (dry-run)
// para os itens da proposta, casando por itemNumber.
//
// ⛔ Entre 11/08/2026 (corte do workspace, d0853d7) e 05/10/2026, o "Aplicar"
// escrevia no formulário da gaveta antiga, que a página de trabalho não lê. O
// toast verde dizia "Adequação aplicada" e a tabela ficava com os preços
// antigos. Por isso esta função devolve QUANTOS itens mudou: quem chama só
// anuncia sucesso quando houve escrita de fato.

export interface LinhaDaAdequacao {
  itemNumber: number;
  unitNovo: number | string;
}

export function aplicarAdequacaoNosItens<T extends { itemNumber: number; unitPrice?: any }>(
  itens: T[],
  linhas: LinhaDaAdequacao[],
): { itens: T[]; aplicados: number } {
  const porNumero = new Map<number, LinhaDaAdequacao>(linhas.map((l) => [Number(l.itemNumber), l]));
  let aplicados = 0;
  const novos = itens.map((it) => {
    const l = porNumero.get(Number(it.itemNumber));
    if (!l) return it;
    aplicados += 1;
    return { ...it, unitPrice: String(l.unitNovo) };
  });
  return { itens: novos, aplicados };
}

export type DecisaoDaAdequacao<T> =
  | { tipo: 'aplicada'; itens: T[]; aplicados: number; meses?: number }
  | { tipo: 'nao-aplicada'; motivo: string };

/**
 * Decide o que o "Aplicar na proposta" faz. Só vira `aplicada` quando a proposta
 * calculada é a que está aberta na página de trabalho E algum item mudou; nos
 * outros casos devolve o motivo, que vira um aviso de erro em vez de um toast
 * verde mentiroso.
 */
export function decidirAdequacao<T extends { itemNumber: number; unitPrice?: any }>(args: {
  resultado: { ok?: boolean; linhas?: LinhaDaAdequacao[]; meses?: number } | null | undefined;
  /** Proposta aberta na página de trabalho (null = nenhuma). */
  propostaNaPagina: number | null;
  /** Proposta para a qual a adequação foi calculada. */
  propostaCalculada: number | null;
  itensDaPagina: T[];
}): DecisaoDaAdequacao<T> {
  const { resultado, propostaNaPagina, propostaCalculada, itensDaPagina } = args;
  if (!resultado?.ok || !Array.isArray(resultado.linhas)) {
    return { tipo: 'nao-aplicada', motivo: 'não há cálculo válido para aplicar.' };
  }
  if (!propostaNaPagina || propostaNaPagina !== propostaCalculada) {
    return { tipo: 'nao-aplicada', motivo: 'a proposta não está aberta na página de trabalho. Abra a proposta e calcule de novo.' };
  }
  const { itens, aplicados } = aplicarAdequacaoNosItens(itensDaPagina, resultado.linhas);
  if (aplicados === 0) {
    return { tipo: 'nao-aplicada', motivo: 'nenhum item da proposta corresponde aos itens calculados.' };
  }
  return { tipo: 'aplicada', itens, aplicados, meses: resultado.meses };
}
